import React, { Component, ErrorInfo, ReactNode } from 'react';
import { getElmahIoLogger } from './elmahIo';

interface ElmahErrorBoundaryProps {
  children: ReactNode;
}

interface ElmahErrorBoundaryState {
  hasError: boolean;
}

/**
 * React Error Boundary recommended by
 * https://docs.elmah.io/logging-to-elmah-io-from-react/
 * Render errors are caught by React and often never reach window.onerror;
 * report them through the logger instance like the Vue/Angular samples.
 */
export class ElmahErrorBoundary extends Component<ElmahErrorBoundaryProps, ElmahErrorBoundaryState> {
  state: ElmahErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ElmahErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const elmah = getElmahIoLogger();
    if (!elmah) {
      return;
    }

    if (errorInfo.componentStack) {
      elmah.addBreadcrumb(errorInfo.componentStack, 'Error', 'error');
    }
    elmah.error(error.message || 'React render error', error);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div role="alert" style={{ padding: 24, textAlign: 'center' }}>
          Something went wrong. Please refresh your browser.
        </div>
      );
    }

    return this.props.children;
  }
}
